import { useState } from 'react';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminFilterBar } from '../../components/primitives/admin-filter-bar.js';
import { AdminSearchInput } from '../../components/primitives/admin-search-input.js';
import { AdminSelect } from '../../components/primitives/admin-select.js';
import { AdminRefreshButton } from '../../components/primitives/admin-refresh-button.js';
import { AdminDataTable, type AdminDataTableColumn } from '../../components/primitives/admin-data-table.js';
import { useDebouncedValue } from '../../lib/use-debounced-value.js';
import { formatCount, formatDateTime } from '../../lib/format.js';
import { useAdminMovements } from '../hooks/use-inventory.js';
import { MOVEMENT_TYPES, type AdminMovement, type MovementType } from '../types/inventory.js';

const MAX_RANGE_DAYS = 90;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoIso(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

export function InventoryMovementsPage() {
  const [searchInput, setSearchInput] = useState('');
  const sku = useDebouncedValue(searchInput, 300);
  const [movementType, setMovementType] = useState('');
  const [dateFrom, setDateFrom] = useState(daysAgoIso(7));
  const [dateTo, setDateTo] = useState(todayIso());
  const [rangeError, setRangeError] = useState<string | null>(null);

  function applyRange(from: string, to: string) {
    const rangeDays = (new Date(to).getTime() - new Date(from).getTime()) / (24 * 60 * 60 * 1000);
    if (rangeDays < 0 || rangeDays > MAX_RANGE_DAYS) {
      setRangeError(`Date range must be between 0 and ${MAX_RANGE_DAYS} days.`);
      return;
    }
    setRangeError(null);
    setDateFrom(from);
    setDateTo(to);
  }

  const { data, isLoading, isError, refetch, isFetching } = useAdminMovements({
    sku: sku || undefined,
    movementType: (movementType || undefined) as MovementType | undefined,
    dateFrom: rangeError ? undefined : `${dateFrom}T00:00:00.000Z`,
    dateTo: rangeError ? undefined : `${dateTo}T23:59:59.999Z`,
    limit: 50,
  });

  const rows = data ?? [];

  const columns: AdminDataTableColumn<AdminMovement>[] = [
    { key: 'createdAt', header: 'Time', render: (row) => formatDateTime(row.createdAt) },
    {
      key: 'productName',
      header: 'Product',
      render: (row) => (
        <div>
          <p className="font-medium text-gray-900">{row.productName}</p>
          <p className="text-xs text-gray-400">
            {row.variantName} · SKU {row.sku}
          </p>
        </div>
      ),
    },
    { key: 'warehouseCode', header: 'Warehouse', render: (row) => row.warehouseCode },
    { key: 'movementType', header: 'Type', render: (row) => row.movementType },
    {
      key: 'quantityDelta',
      header: 'Delta',
      render: (row) => (row.quantityDelta > 0 ? `+${formatCount(row.quantityDelta)}` : formatCount(row.quantityDelta)),
    },
    {
      key: 'balanceAfterOnHand',
      header: 'Balance after',
      render: (row) => `${formatCount(row.balanceAfterOnHand)} on hand / ${formatCount(row.balanceAfterReserved)} reserved`,
    },
    { key: 'referenceType', header: 'Reference', render: (row) => (row.referenceType ? `${row.referenceType}:${row.referenceId}` : '—') },
    { key: 'note', header: 'Reason / note', render: (row) => row.note ?? '—' },
  ];

  return (
    <AdminContent>
      <AdminPageHeader
        title="Inventory movements"
        description="Read-only, append-only stock movement history."
        breadcrumbs={[{ label: 'Inventory', to: '/admin/inventory' }, { label: 'Movements' }]}
        actions={<AdminRefreshButton onRefresh={() => void refetch()} isRefreshing={isFetching} />}
      />

      <AdminSectionCard title="Movement history" className="p-4 sm:p-5">
        <AdminFilterBar>
          <AdminSearchInput value={searchInput} onChange={setSearchInput} placeholder="Search by SKU…" label="Search by SKU" />
          <AdminSelect
            label="Movement type"
            value={movementType}
            onChange={setMovementType}
            allLabel="All types"
            options={MOVEMENT_TYPES.map((type) => ({ value: type, label: type }))}
          />
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <label htmlFor="movements-date-from" className="sr-only">
              From date
            </label>
            <input
              id="movements-date-from"
              type="date"
              value={dateFrom}
              onChange={(event) => applyRange(event.target.value, dateTo)}
              className="focus-visible:ring-brand rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
            <span aria-hidden="true">–</span>
            <label htmlFor="movements-date-to" className="sr-only">
              To date
            </label>
            <input
              id="movements-date-to"
              type="date"
              value={dateTo}
              onChange={(event) => applyRange(dateFrom, event.target.value)}
              className="focus-visible:ring-brand rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </div>
        </AdminFilterBar>
        {rangeError && (
          <p className="mb-2 text-xs text-red-600" role="alert">
            {rangeError}
          </p>
        )}

        <AdminDataTable
          columns={columns}
          rows={rows}
          getRowId={(row) => String(row.id)}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          emptyTitle="No movements in this range"
        />
      </AdminSectionCard>
    </AdminContent>
  );
}
